import { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { LocationFlag } from '@/services/dependabilityV2API';
import styled from '@/styles';
import { dependabilityV2Colors } from '@/styles/colors';
import { exactTime } from '@/util/relativeTime';
import LocationFlagMap from './LocationFlagMap';
import { FlagSolidIcon20, XMarkIcon } from './icons';

/**
 * The Location Flag modal (GRAVY-10365), opened by the "Location flag (N)" badge.
 *
 * A MODAL, not a popover — the AC is explicit that the tier badge's info-icon popover
 * component must not be reused here.
 *
 * Two things the design mocks that are deliberately NOT built:
 *
 *   * no method label ("via QR check-in" / "via phone GPS") — removed by product decision
 *     D4, so the metadata line is date and time only;
 *   * no status pill ("For review" / "Approved") — removed by D6. With the pill gone the
 *     FLAG ICON alone carries state: solid red while unresolved, grey outline once
 *     resolved. They can no longer contradict each other because there is only one of them.
 */

const Header = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});
const Subtitle = styled('div', {
  fontSize: '$lg',
  fontWeight: 400,
  color: dependabilityV2Colors.depInk,
});
const Count = styled('span', {
  fontWeight: 700,
  color: dependabilityV2Colors.depTierMonitored,
});
const Entry = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '12px 0 20px',
  borderBottom: `1px solid ${dependabilityV2Colors.depBorder}`,
  '&:last-of-type': { borderBottom: 'none' },
});
const EntryTop = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
});
const HeadlineBlock = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minWidth: 0,
});
const HeadlineRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});
// Solid red while open, grey outline once resolved — the only carrier of state now that
// the status pill is gone (D6).
const FlagMark = styled('span', {
  flex: 'none',
  display: 'inline-flex',
  variants: {
    resolved: {
      true: { color: dependabilityV2Colors.depMetaText, opacity: 0.7 },
      false: { color: dependabilityV2Colors.depTierMonitored },
    },
  },
});
const Headline = styled('div', {
  fontSize: '$lg',
  color: dependabilityV2Colors.depInk,
  // "Don't truncate the venue name" (AC) — wrap instead.
  overflowWrap: 'anywhere',
});
const Distance = styled('span', { fontWeight: 700 });
const MetaRow = styled('div', {
  paddingLeft: '29px',
  fontSize: '$md',
  color: dependabilityV2Colors.depInk,
});
const ReviewShift = styled(Link, {
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '12px',
  fontSize: '$md',
  fontWeight: 600,
  color: dependabilityV2Colors.depPositive,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
});
// Figma insets these differently: the map is padded 29 on BOTH sides (39583:197179,
// `px-[29px]`) while the caption is padded on the left only (39583:197259, `pl-[29px]`),
// so the caption starts flush with the map but the map does not run to the panel edge.
const MapBlock = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});
const MapInset = styled('div', { padding: '0 29px' });
const CaptionInset = styled('div', { paddingLeft: '29px' });
const Caption = styled('div', {
  fontSize: '$sm',
  color: dependabilityV2Colors.depMetaText,
});
const Note = styled('div', {
  fontSize: '$md',
  color: dependabilityV2Colors.depMetaText,
  padding: '12px 0',
});
const TextButton = styled('button', {
  background: 'none',
  border: 'none',
  padding: 0,
  color: dependabilityV2Colors.depPositive,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '$md',
  alignSelf: 'flex-start',
});
const Footer = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '16px',
  borderTop: `1px solid ${dependabilityV2Colors.depBorder}`,
});
const GoBack = styled('button', {
  minWidth: '100px',
  height: '36px',
  padding: '8px 16px',
  borderRadius: '8px',
  border: `1px solid ${dependabilityV2Colors.depFeedPositive}`,
  backgroundColor: 'white',
  color: dependabilityV2Colors.depPositive,
  fontSize: '$md',
  fontWeight: 600,
  cursor: 'pointer',
});

const ArrowRightIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 10h12m0 0-4.5-4.5M16 10l-4.5 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// "Aug 13, 6:45 PM". Invalid or absent timestamps render as an em dash rather than
// "Invalid Date".
//
// Rendered in the VIEWER's local timezone, matching the shipped ActivityFeed. Worth
// knowing: ops in one timezone reading a flag from a venue in another sees their own clock,
// not the venue's, so a 6:45 PM check-out can read as 2:45 AM. The AC does not say which it
// should be; this follows the existing convention rather than inventing a second one, and —
// as the feed does — the full timestamp stays reachable in a `title` for audits.
export const formatOccurredAt = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'MMM d, h:mm a');
};

/**
 * Where "Review shift" goes (GRAVY-10453): the job page, which lists the job's
 * timesheets regardless of approval state.
 *
 * `order_id` is NOT NULL on jobs, so the fallback is unreachable in practice —
 * but a link to a route with an `undefined` segment silently lands on the legacy
 * dashboard, which is the bug this replaced. Falling back to the order list at
 * least leaves ops somewhere real.
 */
export const reviewShiftPath = (flag: LocationFlag): string => {
  if (!flag.order_id) return '/orders';

  // `?shift=` names the OCCURRENCE (GRAVY-10453 round 2). Without it the job
  // page picked an arbitrary one of the job's shifts — it matched each worker's
  // timesheet on the worker alone and anchored edits to `shifts[0]` — so on a
  // multi-shift job ops landed on the right job showing the wrong day's hours,
  // with nothing on screen saying which. Still the guessing the AC forbids,
  // one level down from the original dead link.
  // `&worker=` names WHOSE row (GRAVY-10453 round 3). `?shift=` got ops to the
  // right occurrence, but the occurrence lists every hired worker — and QA hit a
  // shift with two "John Doe" rows, where nothing said which one carried the
  // flag, so they opened each in turn. Appended rather than replacing `shift`:
  // the two answer different questions (which day, which person) and the page
  // needs both.
  const path = `/orders/${flag.order_id}/jobs/${flag.job_id}?shift=${flag.shift_id}`;
  return flag.worker_id ? `${path}&worker=${flag.worker_id}` : path;
};

// GRAVY-10808: the hover title comes from the shared `exactTime` — this was the
// third private copy of "the full moment on hover", and the one that omitted the
// time ZONE, so an admin in another region read a time that looked local and was
// not. The VISIBLE label stays `formatOccurredAt`: D4 specifies an absolute date
// and time here, not the feed's relative label.

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The card owns the location-flags fetch and passes it down, so the badge and
   * this header render the SAME numbers from the SAME response. Giving the
   * modal its own hook instance meant two fetches that could disagree if a flag
   * resolved between them — precisely the divergence the AC forbids.
   */
  flags: LocationFlag[];
  totalCount: number;
  windowDays: number | null;
  loading: boolean;
  loaded: boolean;
  error: boolean;
  retry: () => void;
}

/**
 * Splits the server's headline around its own distance label, so the distance
 * can be emphasised without this component recomposing the sentence.
 *
 * Falls back to the whole string as `before` when the label is not found —
 * a wording change on the server then renders unstyled rather than blank.
 */
const headlineParts = (
  flag: LocationFlag,
): { before: string; after: string; found: boolean } => {
  const at = flag.distance_label
    ? flag.headline.indexOf(flag.distance_label)
    : -1;
  if (at === -1) return { before: flag.headline, after: '', found: false };
  return {
    before: flag.headline.slice(0, at),
    after: flag.headline.slice(at + flag.distance_label.length),
    found: true,
  };
};

const LocationFlagModal = ({
  isOpen,
  onClose,
  flags,
  totalCount,
  windowDays,
  loading,
  loaded,
  error,
  retry,
}: Props) => {
  const isEmpty = flags.length === 0 && loaded && !loading && !error;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 800,
          maxWidth: '95vw',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } },
      }}
    >
      <DialogTitle
        sx={{
          padding: '24px 24px 8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Header>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Location Flag</div>
          {/* The server's own number. `total_count` includes resolved flags,
              which is deliberate: the header counts what the list shows, and the
              list shows resolved entries too. Since GRAVY-10452 the badge reads
              the same field — it used to read `unresolved_count`, so the two
              disagreed the moment anything was resolved. */}
          <Subtitle data-testid="location-flag-modal-subtitle">
            <Count>{totalCount} flagged</Count>
            {` clock-in/out events in the last ${windowDays ?? 30} days`}
          </Subtitle>
        </Header>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: dependabilityV2Colors.depInk }}
        >
          <XMarkIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '0 24px' }}>
        {!loaded || loading ? <Note>Loading…</Note> : null}

        {error && (
          <Note>
            Couldn&apos;t load location flags.{' '}
            <TextButton type="button" onClick={retry}>
              Try again
            </TextButton>
          </Note>
        )}

        {isEmpty && <Note>No location flags in this period.</Note>}

        {flags.map((flag) => (
          <Entry key={flag.id} data-testid={`location-flag-entry-${flag.id}`}>
            <EntryTop>
              <HeadlineBlock>
                <HeadlineRow>
                  <FlagMark
                    resolved={flag.status === 'resolved'}
                    data-testid={`location-flag-icon-${flag.id}`}
                    data-state={flag.status}
                  >
                    <FlagSolidIcon20 />
                  </FlagMark>
                  {/* Distance bolded inside the sentence, per the design.

                      The sentence itself is the SERVER's `headline`, split around
                      its own `distance_label` rather than recomposed here. The AC
                      requires the modal and the activity feed to word an event
                      identically; the feed renders `headline` verbatim, so
                      rebuilding it from parts on this side is exactly how the two
                      drift. Splitting keeps the text byte-identical and still
                      lets the distance carry its own style. */}
                  <Headline data-testid={`location-flag-headline-${flag.id}`}>
                    {headlineParts(flag).before}
                    {/* Only when the label really is inside the sentence —
                        otherwise rendering it would APPEND a distance the
                        server never wrote. */}
                    {headlineParts(flag).found && (
                      <Distance
                        data-testid={`location-flag-distance-${flag.id}`}
                      >
                        {flag.distance_label}
                      </Distance>
                    )}
                    {headlineParts(flag).after}
                  </Headline>
                </HeadlineRow>
                {/* Date and time only — no method label, no status pill (D4, D6). */}
                <MetaRow
                  data-testid={`location-flag-meta-${flag.id}`}
                  title={exactTime(flag.occurred_at)}
                >
                  {formatOccurredAt(flag.occurred_at)}
                </MetaRow>
              </HeadlineBlock>
              {/* Present on resolved entries too: the AC requires ops be able to revisit
                  something already approved, not only something still pending.

                  GRAVY-10453: this was `/jobs/{job_id}`, which is not a route in this
                  app — it fell through to the legacy dashboard's unfiltered week view,
                  so ops still had to hunt for the shift by hand, which is the exact
                  guessing the link exists to prevent. The real route is
                  `/orders/:order_id/jobs/:job_id`, so the payload now carries `order_id`
                  too.

                  The job page rather than the Dashboard V2 queue on purpose: a RESOLVED
                  flag's shift has usually left the Needs Approval queue, so deep-linking
                  there would show nothing for precisely the revisit case above. */}
              <ReviewShift
                to={reviewShiftPath(flag)}
                data-testid={`location-flag-review-${flag.id}`}
              >
                Review shift
                <ArrowRightIcon />
              </ReviewShift>
            </EntryTop>
            <MapBlock>
              <MapInset>
                <LocationFlagMap
                  anchor={flag.anchor}
                  flagged={flag.flagged_point}
                  geofenceRadiusFeet={flag.radius_feet}
                  testId={`location-flag-map-${flag.id}`}
                />
              </MapInset>
              {/* This entry's OWN caption. The design repeats one caption under both maps;
                  the AC calls that a bug, and the server derives it per row. */}
              <CaptionInset>
                <Caption data-testid={`location-flag-caption-${flag.id}`}>
                  {`${flag.distance_label} away from ${flag.venue_address}`}
                </Caption>
              </CaptionInset>
            </MapBlock>
          </Entry>
        ))}
      </DialogContent>

      <Footer>
        <GoBack type="button" onClick={onClose}>
          Go back
        </GoBack>
      </Footer>
    </Dialog>
  );
};

export default LocationFlagModal;
